import { useCallback, useMemo } from 'react'

import { Box, Button, Checkbox, Divider, Group, Stack, Title } from '@mantine/core'
import { isEmail, useForm } from '@mantine/form'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import {
  RoleAttributesApiDTO,
  UserApiDTO,
  UserScopesApiDTOCmsItem,
} from '../../codegen/generated/Api.schemas'
import { usePatchApiUsersIdRoles, usePatchApiUsersIdScopes } from '../../codegen/generated/users'

import { notifications } from '@mantine/notifications'
import { LXLoader } from '../../components/LXLoader'
import { UsersHelper } from '../../helpers/Users.helper'
import { useUserStore } from '../../stores/useUserStore'
import { Forbidden } from '../Forbidden'
import { UserRolesCards } from './UserRolesCards'
import { UserScopes } from './UserScopes'

type UpdateUserProps = {
  user?: UserApiDTO
  onUpdate: () => void
}

export const UpdateUser = ({ user, onUpdate }: UpdateUserProps) => {
  const t = useTranslate()
  const userStore = useUserStore()

  const roleIds = useMemo(() => (user?.roles ?? []).map(r => r.id), [user?.roles])
  const form = useForm({
    initialValues: {
      email: user?.email ?? '',
      roleIds,
      scopes: user?.scopes,
    },
    validate: {
      email: isEmail(),
      roleIds: (value: RoleAttributesApiDTO['id'][]) => value.length === 0,
    },
  })

  const updateUserRolesMutation = usePatchApiUsersIdRoles()
  const updateUserScopesMutation = usePatchApiUsersIdScopes()

  const onUpdateUser = useCallback(async () => {
    try {
      if (user?.id) {
        const { roleIds, scopes } = form.values
        await updateUserRolesMutation.mutateAsync({
          id: user.id,
          data: {
            roleIds: roleIds.includes(UsersHelper.ADMIN_ROLE) ? [UsersHelper.ADMIN_ROLE] : roleIds,
          },
        })

        if (scopes) {
          await updateUserScopesMutation.mutateAsync({
            id: user.id,
            data: {
              scopes: {
                cms:
                  scopes.cms.length > 0 && !roleIds.includes(UsersHelper.ADMIN_ROLE)
                    ? (scopes.cms as (keyof typeof UserScopesApiDTOCmsItem)[])
                    : ['*'],
                beneficiariesIds:
                  scopes.beneficiariesIds.length > 0 && !roleIds.includes(UsersHelper.ADMIN_ROLE)
                    ? scopes.beneficiariesIds
                    : ['*'],
                youtubeChannelsIds:
                  scopes.youtubeChannelsIds.length > 0 && !roleIds.includes(UsersHelper.ADMIN_ROLE)
                    ? scopes.youtubeChannelsIds
                    : ['*'],
              },
            },
          })
        }
        onUpdate()
      }
    } catch (error) {
      console.error(error)
      notifications.show({
        color: 'red',
        title: `${t('_generics.error')} - ${t('pages.users.errors.userUpdate.title')}`,
        message: t('pages.users.errors.userUpdate.message'),
      })
    }
  }, [
    form.values,
    updateUserRolesMutation.mutateAsync,
    updateUserScopesMutation.mutateAsync,
    user?.id,
    onUpdate,
    t,
  ])

  const onRoleIdsChange = useCallback(
    (roleIds: RoleAttributesApiDTO['id'][]) => {
      form.setValues({
        roleIds,
      })
    },
    [form.setValues],
  )

  if (!user) {
    return <LXLoader />
  }

  return (
    <form onSubmit={form.onSubmit(onUpdateUser)}>
      <Stack>
        <Title>{user?.email}</Title>
        <Divider my="md" />
        <Stack gap="xl">
          <Box>
            <Title order={4} mb="xs">
              {t('pages.users.scope')}
            </Title>
            {userStore.isAuthorised('user_rights.edit') ? (
              <UserScopes
                scopes={user.scopes}
                onUpdate={scopes => {
                  form.setValues({
                    scopes,
                  })
                }}
              />
            ) : (
              <Forbidden permission="user_rights.edit" />
            )}
          </Box>
          <Box>
            <Title order={4}>{t('pages.users.roles')}</Title>
            {userStore.isAuthorised('user_rights.edit') ? (
              <Checkbox.Group
                value={form.values.roleIds}
                defaultValue={roleIds}
                onChange={values => onRoleIdsChange(values as RoleAttributesApiDTO['id'][])}
              >
                <UserRolesCards />
              </Checkbox.Group>
            ) : (
              <Forbidden permission="user_rights.edit" />
            )}
          </Box>
        </Stack>
      </Stack>
      <Group justify="flex-start" mt="xl">
        <Button
          type="submit"
          size="compact-md"
          leftSection={<IconDeviceFloppy size={16} />}
          disabled={!form.isValid() || !form.isDirty()}
          loading={updateUserRolesMutation.isLoading}
        >
          {t('_generics.save')}
        </Button>
      </Group>
    </form>
  )
}
